// =============================================================================
// ENTREPRISE SEARCH - Composant de recherche d'entreprise via API SIREN
// =============================================================================
// Recherche par nom ou SIREN via recherche-entreprises.api.gouv.fr
// Auto-remplissage des données entreprise
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { Building2, Loader2, X, Search, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = 'https://recherche-entreprises.api.gouv.fr/search';

export const EntrepriseSearch = ({
  value = '',
  onChange,
  onSelect,
  placeholder = "Rechercher par nom ou SIREN...",
  className,
  disabled = false,
  minChars = 3,
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  // Fermer le dropdown si clic en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    if (query.length < minChars) {
      setSuggestions([]);
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const cleanQuery = query.replace(/\s/g, '');
        const isSiren = /^\d{9,14}$/.test(cleanQuery);
        
        const url = `${API_URL}?q=${isSiren ? cleanQuery : encodeURIComponent(query)}&per_page=6`;
        const res = await fetch(url);
        const data = await res.json();
        
        if (data.results && data.results.length > 0) {
          setSuggestions(data.results.map(r => ({
            siren: r.siren,
            siret: r.siege?.siret,
            nom: r.nom_complet || r.nom_raison_sociale,
            forme_juridique: r.nature_juridique || "",
            adresse: r.siege?.adresse || "",
            code_postal: r.siege?.code_postal || "",
            ville: r.siege?.libelle_commune || "",
            activité: r.siege?.activite_principale || "",
            dirigeant: r.dirigeants?.[0] ? 
              `${r.dirigeants[0].prenoms || ''} ${r.dirigeants[0].nom || ''}`.trim() : null
          })));
          setShowDropdown(true);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        console.error('Erreur API Entreprise:', err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query, minChars]);

  const handleSelect = (suggestion) => {
    setQuery(suggestion.nom);
    setShowDropdown(false);
    setSuggestions([]);
    
    // Mapper forme juridique
    const mapFormeJuridique = (code) => {
      const mapping = {
        '5710': 'SAS', '5720': 'SASU', '5498': 'EURL',
        '5499': 'SARL', '5599': 'SA', '1000': 'EI',
        '5498': 'EURL', '9220': 'Association'
      };
      return mapping[code] || code || '';
    };
    
    onSelect?.({
      raison_sociale: suggestion.nom,
      siren: suggestion.siren,
      siret: suggestion.siret,
      forme_juridique: mapFormeJuridique(suggestion.forme_juridique),
      adresse: suggestion.adresse,
      code_postal: suggestion.code_postal,
      ville: suggestion.ville,
      activité: suggestion.activité,
      dirigeant: suggestion.dirigeant
    });
    
    onChange?.(suggestion.nom);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  const clearInput = () => {
    setQuery('');
    setSuggestions([]);
    onChange?.('');
    inputRef.current?.focus();
  };

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange?.(e.target.value);
          }}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("pl-10 pr-10 bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]", className)}
        />
        {loading ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)] animate-spin" />
        ) : query && (
          <button
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)]"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown des suggestions */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg shadow-xl max-h-80 overflow-auto"
        >
          {suggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(suggestion)}
              className={cn(
                "w-full text-left px-4 py-3 hover:bg-[var(--oh-bg-elevated)] transition-colors",
                "flex flex-col border-b border-[var(--oh-border)] last:border-0",
                idx === selectedIndex && "bg-[var(--oh-bg-elevated)]"
              )}
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[var(--oh-accent-text)]" />
                <span className="text-[var(--oh-text)] font-medium text-sm">
                  {suggestion.nom}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-[var(--oh-text-secondary)]">
                <span>SIREN: {suggestion.siren}</span>
                <span>•</span>
                <span>{suggestion.code_postal} {suggestion.ville}</span>
              </div>
              {suggestion.dirigeant && (
                <div className="text-xs text-[var(--oh-success-text)] mt-1">
                  Dirigeant: {suggestion.dirigeant}
                </div>
              )}
            </button>
          ))}
          <div className="px-4 py-2 text-xs text-[var(--oh-text-secondary)] bg-[var(--oh-bg-surface)]">
            Recherche via recherche-entreprises.api.gouv.fr
          </div>
        </div>
      )}
    </div>
  );
};

export default EntrepriseSearch;
